/**
 * GenericSDGContribution — /project/:id/sdg
 * Housing shell standard. SDG data driven by sector_config.sdg_contributions.
 * No hardcoded SDG lists — content is sector-configurable.
 */
import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { RefreshCw, ArrowLeft } from "lucide-react";
import { useProjectWorkspace } from "@/hooks/useProjectWorkspace";

import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
// ── SDG contribution type ────────────────────────────────────────────────────
const CONTRIBUTION_TYPE = {
  13: { type: "Direct",     source: "annual_reduction_tco2e",  sourceLabel: "Annual GHG Reduction",      color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  7:  { type: "Direct",     source: "electricity_generated",   sourceLabel: "Electricity Generated",      color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  12: { type: "Direct",     source: "waste_diverted_tonnes",   sourceLabel: "Waste Diverted",             color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  6:  { type: "Indirect",   source: "digestate_output_tonnes", sourceLabel: "Digestate Output",           color: "bg-blue-100 text-blue-700 border-blue-200" },
  11: { type: "Conditional",source: null,                      sourceLabel: "Waste Management Coverage",  color: "bg-amber-100 text-amber-700 border-amber-200" },
  8:  { type: "Indirect",   source: "annual_savings_usd",      sourceLabel: "Annual Revenue",             color: "bg-blue-100 text-blue-700 border-blue-200" },
  9:  { type: "Conditional",source: null,                      sourceLabel: "Technology Infrastructure",  color: "bg-amber-100 text-amber-700 border-amber-200" },
};
const CONTRIBUTION_NOTE = {
  Direct:      "Primary project output directly advances this goal.",
  Indirect:    "Secondary co-benefit from project operation.",
  Conditional: "Potential contribution — site-specific evidence required.",
};
const SDG_COLORS = {
  6:  { bg: "bg-blue-50",    border: "border-blue-200",    text: "text-blue-800",    dot: "bg-blue-400"    },
  7:  { bg: "bg-yellow-50",  border: "border-yellow-200",  text: "text-yellow-800",  dot: "bg-yellow-400"  },
  8:  { bg: "bg-amber-50",   border: "border-amber-200",   text: "text-amber-800",   dot: "bg-amber-500"   },
  9:  { bg: "bg-orange-50",  border: "border-orange-200",  text: "text-orange-800",  dot: "bg-orange-400"  },
  11: { bg: "bg-teal-50",    border: "border-teal-200",    text: "text-teal-800",    dot: "bg-teal-400"    },
  12: { bg: "bg-amber-50",   border: "border-amber-200",   text: "text-amber-800",   dot: "bg-amber-400"   },
  13: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-800", dot: "bg-emerald-500" },
};
const REL_COLOR = {
  High:   "bg-emerald-100 text-emerald-700 border-emerald-300",
  Medium: "bg-amber-100   text-amber-700   border-amber-300",
  Low:    "bg-slate-100   text-slate-500   border-slate-200",
};
const DEFAULT_COLOR = { bg: "bg-slate-50", border: "border-slate-200", text: "text-slate-700", dot: "bg-slate-400" };

export default function GenericSDGContribution() {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    loading, isEmpty, sectorLabel, org, calcVersion, reduction,
    sdgContributions, sector,
  } = useProjectWorkspace(id);

  if (loading) return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <RefreshCw size={18} className="animate-spin text-teal-500" />
    </div>
  );

  const sdgs = sdgContributions || [];
  const highCount   = sdgs.filter(s => s.relevance === "High").length;
  const mediumCount = sdgs.filter(s => s.relevance === "Medium").length;

  return (
    <div className="p-6 lg:p-8 min-h-screen">

        <button onClick={() => navigate(`/project/${id}/workspace`)}
          className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 mb-5">
          <ArrowLeft size={13} /> {sectorLabel}
        </button>

        <div className="mb-6">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-0.5">{org.name}</p>
          <PageHeader title="SDG Contribution" />
          <p className="text-xs text-slate-400 mt-0.5">
            Sustainable Development Goal alignment · {calcVersion ? `v${calcVersion.number}` : "No calculation yet"}
          </p>
        </div>

        {sdgs.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
            <p className="text-sm font-bold text-slate-600">SDG contributions not defined for this sector.</p>
            <p className="text-xs text-slate-400 mt-1">Sector config needs sdg_contributions array.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

            {/* Left 2/3 — SDG table */}
            <div className="lg:col-span-2 space-y-4">
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="px-5 py-3 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                  <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold">SDG Alignment</p>
                  <span className="text-xs text-slate-400">{sdgs.length} SDGs · {highCount} High · {mediumCount} Medium</span>
                </div>
                <div className="divide-y divide-slate-50">
                  {sdgs.map(sdg => {
                    const c = SDG_COLORS[sdg.id] || DEFAULT_COLOR;
                    return (
                      <div key={sdg.id} className={`px-5 py-4 ${c.bg}`} data-testid={`sdg-${sdg.id}`}>
                        <div className="flex items-start gap-4">
                          <div className={`w-10 h-10 rounded-xl border-2 ${c.border} flex items-center justify-center flex-shrink-0`}>
                            <span className={`text-sm font-bold ${c.text}`}>{sdg.id}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-3 mb-1">
                              <p className={`text-xs font-bold ${c.text}`}>SDG {sdg.id} — {sdg.name}</p>
                              <div className="flex items-center gap-1.5 flex-shrink-0">
                                {CONTRIBUTION_TYPE[sdg.id] && (
                                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${CONTRIBUTION_TYPE[sdg.id].color}`}>
                                    {CONTRIBUTION_TYPE[sdg.id].type}
                                  </span>
                                )}
                                <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${REL_COLOR[sdg.relevance] || REL_COLOR.Low}`}>
                                  {sdg.relevance}
                                </span>
                              </div>
                            </div>
                            <p className="text-xs text-slate-600 mb-1.5 leading-snug">{sdg.driver}</p>
                            {/* Contribution type note */}
                            {CONTRIBUTION_TYPE[sdg.id] && (
                              <p className="text-xs text-slate-400 italic mb-1">{CONTRIBUTION_NOTE[CONTRIBUTION_TYPE[sdg.id].type]}</p>
                            )}
                            {sdg.indicator && (
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-slate-400 font-semibold uppercase tracking-wide">Indicator:</span>
                                <span className="text-xs text-slate-500 italic">{sdg.indicator}</span>
                              </div>
                            )}
                            {/* Source metric */}
                            {CONTRIBUTION_TYPE[sdg.id]?.sourceLabel && (
                              <div className="mt-1 flex items-center gap-1">
                                <span className="text-xs text-slate-400 font-semibold uppercase tracking-wide">Source metric:</span>
                                <span className="text-xs text-slate-500">{CONTRIBUTION_TYPE[sdg.id].sourceLabel}</span>
                              </div>
                            )}
                            {sdg.quantified_value != null && (
                              <div className="mt-1.5 inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 rounded-lg px-2 py-1">
                                <span className="text-xs font-bold text-emerald-700">
                                  {typeof sdg.quantified_value === "number"
                                    ? sdg.quantified_value.toLocaleString()
                                    : String(sdg.quantified_value)}
                                  <span className="font-normal text-emerald-600 ml-1">{sdg.impact_unit}</span>
                                </span>
                                <span className="text-xs text-emerald-500">{sdg.impact_label}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Methodology note */}
              <SectionCard>

                <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-2">Methodology Note</p>
                <p className="text-xs text-slate-500 leading-relaxed">
                  SDG contribution assessment is structured in three tiers:
                  <strong className="text-slate-700"> Direct</strong> — primary project output directly advances the goal
                  (e.g. electricity generated → SDG 7, GHG reduction → SDG 13);
                  <strong className="text-slate-700"> Indirect</strong> — secondary co-benefit from project operation
                  (e.g. digestate → SDG 6);
                  <strong className="text-slate-700"> Conditional</strong> — potential contribution requiring site-specific evidence.
                  Quantified values are calculated estimates from project outputs — not certified SDG claims.
                  Independent sustainability assessment is required before use in formal reporting.
                </p>
                        </SectionCard>
            </div>

            {/* Right 1/3 — Summary */}
            <div className="space-y-4">
              <div className="bg-[#0F172A] rounded-xl p-5">
                <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-3">SDG Summary</p>
                <div className="space-y-2">
                  {[
                    ["Total SDGs",   sdgs.length,   "text-white"],
                    ["High relevance", highCount,    "text-emerald-400"],
                    ["Medium relevance", mediumCount, "text-amber-400"],
                  ].map(([l, v, c]) => (
                    <div key={l} className="bg-white/5 rounded-xl px-3 py-2.5 flex items-center justify-between">
                      <p className="text-xs text-slate-500">{l}</p>
                      <p className={`text-lg font-bold ${c}`}>{v}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Climate impact connection */}
              {reduction && (
                <SectionCard>

                  <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-3">Climate Context</p>
                  <p className="text-xs text-slate-600 leading-relaxed mb-3">
                    This project&apos;s direct climate contribution is the primary SDG 13 driver. All other SDG contributions
                    flow from the project&apos;s operational outputs.
                  </p>
                  <div className="bg-emerald-50 rounded-xl px-3 py-2">
                    <p className="text-xs text-slate-400">Annual GHG reduction</p>
                    <p className="text-sm font-bold text-emerald-700">
                      ~{Number(reduction.tco2e_yr || reduction.total_tco2e_yr || 0).toLocaleString()} tCO₂e/yr
                    </p>
                  </div>
                          </SectionCard>
              )}

              {/* Reporting alignment */}
              <div className="bg-white border border-slate-200 rounded-xl p-4">
                <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-2">Reporting Alignment</p>
                <div className="space-y-1.5">
                  {["GRI Standards (Climate/Energy)", "TCFD Voluntary Framework", "Voluntary Carbon Market — SDG co-benefits"].map(f => (
                    <div key={f} className="flex items-center gap-1.5 text-xs text-slate-500">
                      <span className="w-1.5 h-1.5 rounded-full bg-teal-400 flex-shrink-0" />
                      {f}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
    </div>
  );
}
